import { useEffect, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cdrErr, cycleText } from "./cdrMeta";

function Choice({ on, onClick, title, hint, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${on ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"}`}>
      <span className="block text-sm font-medium text-slate-800">{title}</span><span className="block text-[11px] text-slate-500">{hint}</span>
    </button>
  );
}

/** Send the files of the chosen recipients — to yourself (test) or for real. */
export function CdrSendDialog({ open, onClose, chosen, ov, onSent }) {
  const [f, setF] = useState({ cycle_key: "", start: "", end: "", test: true, confirm: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ cycle_key: ov.cycles[0]?.key || "", start: "", end: "", test: true, confirm: false }); }, [open, ov.cycles]);
  const send = async () => {
    setBusy(true);
    const body = { recipient_ids: chosen.map((r) => r.id), test: f.test, confirm: f.confirm,
      ...(f.cycle_key ? { cycle_key: f.cycle_key } : { start: f.start, end: f.end }) };
    try { await api.post("/cdr-dispatch/send", body); toast.success(f.test ? `Sending ${chosen.length} file(s) to ${ov.me}` : `Sending ${chosen.length} file(s)`); onSent(); }
    catch (e) { toast.error(cdrErr(e, "Could not send")); } finally { setBusy(false); }
  };
  const ready = (f.cycle_key || (f.start && f.end)) && (f.test || f.confirm);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cdr-send-dialog">
        <DialogHeader><DialogTitle>Send CDR files</DialogTitle>
          <DialogDescription>{chosen.length} recipient(s): {chosen.slice(0, 6).map((r) => r.name).join(", ")}{chosen.length > 6 ? "…" : ""}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">Billing cycle</span>
            <select value={f.cycle_key} onChange={(e) => setF({ ...f, cycle_key: e.target.value })} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="cdr-send-cycle">
              {ov.cycles.map((c) => <option key={c.key} value={c.key}>{cycleText(c)}</option>)}
              <option value="">Custom dates…</option></select></label>
          {!f.cycle_key && (
            <div className="flex gap-2">
              <Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="h-9" data-testid="cdr-send-start" />
              <Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="h-9" data-testid="cdr-send-end" />
            </div>)}
          <div className="space-y-2">
            <Choice on={f.test} onClick={() => setF({ ...f, test: true })} title="Test — send every file to me" hint={`All files go to ${ov.me}, marked [TEST]. No copy to the billing mailbox.`} testid="cdr-send-test" />
            <Choice on={!f.test} onClick={() => setF({ ...f, test: false })} title="Send to the recipients" hint="Real e-mails to each territory or domain, with the BCC copy." testid="cdr-send-real" />
          </div>
          {!f.test && (
            <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <Checkbox checked={f.confirm} onCheckedChange={(v) => setF({ ...f, confirm: !!v })} data-testid="cdr-send-confirm" /> Partners and customers will get these e-mails.</label>)}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cdr-send-cancel">Cancel</Button>
          <Button onClick={send} disabled={busy || !ready} data-testid="cdr-send-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {f.test ? "Send test" : "Send"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
